import type { GenericSchema, InferOutput } from 'valibot'
import { array, boolean, intersect, literal, nullish, number, object, optional, pipe, safeParse, string, transform, union, unknown } from 'valibot'
import { normalizeJuridikaImageUrl } from './juridika-media-image'
import { parsePagination } from '#shared/utils/journal-pagination'

// All journals
// https://ss929090-2ape21mx-eu-west-1-aws.searchstax.com/solr/#/juridika-content-test/query?q=nodeType:journal&q.op=OR&indent=true

// Example of a publishing
// https://ss929090-2ape21mx-eu-west-1-aws.searchstax.com/solr/#/juridika-content-test/query?q=nodeType:journal_publisher&q.op=OR&indent=true&fl=*,%5Bchild%20limit%3D1000%5D

const journalNodeTypeSchema = union([
  literal('journal'),
  literal('journal_colophon'),
  literal('journal_editorial'),
  literal('journal_section'),
  literal('journal_toc'),
  literal('journal_article'),
  literal('journal_footnote'),
])

export const baseNodeSchema = object({
  id: string(),
  nodeType: journalNodeTypeSchema,
  title: optional(string()),
})

export const sectionSchema = object({
  ...baseNodeSchema.entries,
  pageNumber: optional(array(number())),
  pageNumberString: optional(string()),
  bodytext: optional(string()),
  pageCounter: optional(number()),
  pagination: optional(pipe(unknown(), transform(parsePagination))),
})

export const journalFootnoteSchema = object({
  footnoteId: optional(string()),
  bodytext: optional(string()),
})

const articleTypeSchema = union([
  literal('editorial'),
  literal('research-article'),
  literal('review-article'),
  literal('book-review'),
  literal('discussion'),
  literal('bibliography'),
  literal('other'),
  literal('retraction'),
  literal('correction'),
  literal('erratum'),
  literal('addendum'),
  literal('expression-of-concern'),
  literal('withdrawal'),
])

function normalizeToFlatArray(value: unknown): unknown[] {
  if (!Array.isArray(value)) {
    return [value]
  }
  return value.flat(Infinity)
}

/**
 * Coerces a value into an array of `itemSchema` outputs: wraps a single item
 * into an array, flattens nested arrays, and drops items that fail validation
 * so one corrupted entry doesn't take down the whole field.
 */
function normalizeArrayField<TSchema extends GenericSchema>(itemSchema: TSchema) {
  return pipe(
    union([
      array(unknown()),
      itemSchema,
    ]),
    transform((value): InferOutput<TSchema>[] => {
      const validItems: InferOutput<TSchema>[] = []
      for (const item of normalizeToFlatArray(value)) {
        const result = safeParse(itemSchema, item)
        if (result.success) {
          validItems.push(result.output)
        }
      }
      return validItems
    }),
  )
}

/**
 * Article schema (shared leder and articles)
 */
export const journalArticleSchema = object({
  ...baseNodeSchema.entries,
  doi: optional(normalizeArrayField(string())),
  abstract: optional(pipe(
    normalizeArrayField(string()),
    transform(it => it?.[0]),
  )),
  firstPage: optional(number()),
  lastPage: optional(number()),
  publish_date: optional(pipe(
    normalizeArrayField(string()),
    transform(it => it?.[0]),
  )),
  articleType: optional(normalizeArrayField(articleTypeSchema)),
  subject: optional(normalizeArrayField(string())),
  hasOriginalPdf: optional(boolean()),
  pdfUri: optional(string()),
  pdfUrl: optional(normalizeArrayField(string())),
  author: optional(normalizeArrayField(string())),
  author_list: optional(normalizeArrayField(string())),
  keywords: optional(normalizeArrayField(string())),
  keyword: optional(normalizeArrayField(string())),
  sections: optional(normalizeArrayField(sectionSchema)),
  footnotes: optional(normalizeArrayField(journalFootnoteSchema)),
  copyrightText: optional(string()),
})

export type JournalArticle = InferOutput<typeof journalArticleSchema>

export const kolofonSchema = object({
  ...baseNodeSchema.entries,
  bodytext: optional(string()),
})

export const tocSchema = object({
  ...baseNodeSchema.entries,
  tocEntry: optional(array(string())),
})

export const baseJournalSchema = intersect([
  baseNodeSchema,
  object({
    publisherId: nullish(string()),
    doi: nullish(array(string())),
    publisher: nullish(array(string())),
    year: nullish(string()),
    issn: nullish(array(string())),
    volume: nullish(string()),
    issue: nullish(string()),
    issueDoi: nullish(array(string())),
    coverImage: nullish(union([string(), array(string())])),
    coverImageLarge: nullish(union([string(), array(string())])),
    pdfUri: nullish(string()),
    path_s: nullish(array(string())),
    legal_areas: nullish(array(string())),
  }),
])

export const journalSchema = intersect([
  baseJournalSchema,
  object({
    kolofon: optional(kolofonSchema),
    toc: optional(tocSchema),
    leder: optional(journalArticleSchema),
    articles: optional(normalizeArrayField(journalArticleSchema)),
  }),
])

export const journalListSchema = pipe(
  journalSchema,
  transform((it) => {
    const { issueDoi, doi, publisherId, issn, path_s, leder, title, ...rest } = it

    // Extract author from leder (editorial) article
    const author = leder?.author?.[0]

    return {
      ...rest,
      coverImage: normalizeJuridikaImageUrl(rest.coverImage),
      coverImageLarge: normalizeJuridikaImageUrl(rest.coverImageLarge),
      issueDoi: issueDoi?.[0],
      doi: doi?.[0],
      publisherId,
      title,
      issn: issn?.[0],
      path: path_s?.[0],
      author,
      leder,
    }
  }),
)

export type BaseJournal = InferOutput<typeof journalSchema>
export type JournalListItem = InferOutput<typeof journalListSchema>
export type JournalFootnote = InferOutput<typeof journalFootnoteSchema>
